import { TrackingLatLng } from './orderTracking.interface';
import { distanceKm } from './orderTracking.utils';

export interface TruckMotionTarget {
	id: number;
	lat: number;
	lng: number;
	/** epoch ms of the GPS fix; without it the default duration is used */
	at?: number;
	/** a real GPS fix, not a place computed from the route progress */
	gps: boolean;
	/** 0..1 along the route; slid with the marker so the drawn route keeps up */
	progress?: number;
}

export interface TruckMotionOptions {
	/** `false` puts every marker straight on its target, as without motion */
	enabled: boolean;
	/** when the fixes carry no time */
	defaultDurationMs: number;
	minDurationMs: number;
	maxDurationMs: number;
	/** farther than this the truck jumps: a signal gap, not a drive */
	teleportDistanceM: number;
	/** longer than this between fixes the truck jumps as well */
	teleportGapMs: number;
}

/** Where to draw one marker right now; `target` is where Leaflet holds it. */
export interface TruckMotionFrame {
	id: number;
	position: TrackingLatLng;
	target: TrackingLatLng;
	/** route progress at `position`, when the target carries one */
	progress?: number;
}

interface Segment {
	from: TrackingLatLng;
	to: TrackingLatLng;
	fromProgress?: number;
	startedAt: number;
	duration: number;
}

interface TruckState {
	target: TrackingLatLng;
	targetAt?: number;
	progress?: number;
	segment?: Segment;
}

const samePlace = (a: TrackingLatLng, b: TrackingLatLng): boolean =>
	a.lat === b.lat && a.lng === b.lng;

const slideRatio = (segment: Segment, now: number): number =>
	Math.min(Math.max((now - segment.startedAt) / segment.duration, 0), 1);

const interpolate = (segment: Segment, now: number): TrackingLatLng => {
	const t = slideRatio(segment, now);
	return {
		lat: segment.from.lat + (segment.to.lat - segment.from.lat) * t,
		lng: segment.from.lng + (segment.to.lng - segment.from.lng) * t,
	};
};

// Moves at the same pace as the marker, so the route drawn up to the truck
// ends under it the whole slide.
const interpolateProgress = (
	segment: Segment,
	to: number | undefined,
	now: number,
): number | undefined =>
	segment.fromProgress === undefined || to === undefined
		? to
		: segment.fromProgress +
		  (to - segment.fromProgress) * slideRatio(segment, now);

/**
 * Glides each truck from the fix it showed to the fix that just came in.
 *
 * Pure bookkeeping, no Leaflet: a target per truck (where the marker really
 * is, as far as the map data goes) and, while a drive is on, the segment the
 * marker is sliding along. `tick` says where every moving marker should be
 * drawn right now; whoever owns the markers puts them there.
 *
 * The owner must leave the marker itself on its target and draw only its
 * element along the slide: leaflet.markercluster drops and re-adds a marker
 * on every `setLatLng`, so sliding through Leaflet would rebuild the marker
 * on every frame.
 *
 * A new fix starts the slide from wherever the marker is drawn at that
 * moment, so a fix arriving mid-slide bends the path instead of snapping the
 * marker back. The slide takes as long as the two fixes are apart in time
 * (clamped), so the drawn speed matches the driven one. Far jumps and long
 * gaps are not driven but teleported: there was no road under them.
 *
 * A target may carry its route progress; it slides along with the place, so
 * the route coloured up to the truck grows in step with the marker.
 */
export class TruckMotionController {
	private trucks = new Map<number, TruckState>();

	constructor(private options: TruckMotionOptions) {}

	get enabled(): boolean {
		return this.options.enabled;
	}

	/** Switching off ends the slides; the frames returned finish the drawing. */
	setEnabled(enabled: boolean): TruckMotionFrame[] {
		this.options = { ...this.options, enabled };
		return enabled ? [] : this.snapAll();
	}

	/** Where the truck is drawn right now: mid-slide, or on its target. */
	displayed(id: number, now: number): TrackingLatLng | undefined {
		const truck = this.trucks.get(id);
		if (!truck) return undefined;
		return truck.segment ? interpolate(truck.segment, now) : truck.target;
	}

	/** The route progress drawn right now, in step with `displayed`. */
	displayedProgress(id: number, now: number): number | undefined {
		const truck = this.trucks.get(id);
		if (!truck) return undefined;
		return truck.segment
			? interpolateProgress(truck.segment, truck.progress, now)
			: truck.progress;
	}

	inFlight(): boolean {
		for (const truck of this.trucks.values()) {
			if (truck.segment) return true;
		}
		return false;
	}

	/** Takes a fresh fix for one truck; starts a slide towards it or jumps. */
	setTarget(target: TruckMotionTarget, now: number): void {
		const { id, at, gps, progress } = target;
		const to = { lat: target.lat, lng: target.lng };
		const truck = this.trucks.get(id);
		if (!truck) {
			this.trucks.set(id, { target: to, targetAt: at, progress });
			return;
		}
		const targetAt = at ?? truck.targetAt;
		if (samePlace(truck.target, to)) {
			truck.targetAt = targetAt;
			truck.progress = progress;
			return;
		}
		const from = this.displayed(id, now) ?? truck.target;
		const fromProgress = this.displayedProgress(id, now);
		const gap =
			at !== undefined && truck.targetAt !== undefined
				? at - truck.targetAt
				: undefined;
		const teleport =
			!this.options.enabled ||
			!gps ||
			distanceKm(from, to) * 1000 > this.options.teleportDistanceM ||
			(gap !== undefined && (gap <= 0 || gap > this.options.teleportGapMs));
		if (teleport) {
			this.trucks.set(id, { target: to, targetAt, progress });
			return;
		}
		const duration = Math.min(
			Math.max(
				gap ?? this.options.defaultDurationMs,
				this.options.minDurationMs,
			),
			this.options.maxDurationMs,
		);
		this.trucks.set(id, {
			target: to,
			targetAt,
			progress,
			segment: { from, to, fromProgress, startedAt: now, duration },
		});
	}

	/** Forgets trucks that are no longer on the map. */
	keepOnly(ids: Iterable<number>): void {
		const keep = new Set(ids);
		Array.from(this.trucks.keys()).forEach((id) => {
			if (!keep.has(id)) this.trucks.delete(id);
		});
	}

	/**
	 * Ends every slide at once. Returns a frame per slide that was on, so the
	 * markers can be drawn on their targets right away.
	 */
	snapAll(): TruckMotionFrame[] {
		return this.tick(Infinity);
	}

	/**
	 * Advances the slides to `now`. Returns a frame for every truck still
	 * moving, plus one last frame for a slide that just finished, so the
	 * marker ends exactly on its target.
	 */
	tick(now: number): TruckMotionFrame[] {
		const frames: TruckMotionFrame[] = [];
		this.trucks.forEach((truck, id) => {
			const { segment } = truck;
			if (!segment) return;
			const done = now - segment.startedAt >= segment.duration;
			frames.push({
				id,
				position: done ? truck.target : interpolate(segment, now),
				target: truck.target,
				progress: done
					? truck.progress
					: interpolateProgress(segment, truck.progress, now),
			});
			if (done) delete truck.segment;
		});
		return frames;
	}
}
